import React, {useCallback, useRef, useState} from 'react';
import {CircularProgress, Divider, Stack, Typography} from '@mui/material';
import {useNavigate, useParams} from 'react-router';
import {nanoid} from 'nanoid';
import MessageComposer from '../../../../../components/MessageComposer';
import Messages from './Messages';
import {useFetchMessages} from '../../../hooks/useFetchMessages';
import useChatMessagesStore from '../../../store/useChatMessagesStore';
import usePolicyPageStore from '../../../store/usePolicyPageStore';
import {useSession} from '../../../../../firebase/UserProvider';

const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function _authH() {
    const t = localStorage.getItem('access_token');
    return t
        ? {'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json'}
        : {'Content-Type': 'application/json'};
}

async function createFintelChat(chatId, title, uid) {
    await fetch(`${_awpBase}/api/chats`, {
        method: 'POST',
        headers: _authH(),
        body: JSON.stringify({
            id: chatId,
            title,
            chatType: 'fintel',
            owner: uid,
            participants: [uid],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        }),
    });
}

async function persistMessage(chatId, msg) {
    await fetch(`${_awpBase}/api/chats/${encodeURIComponent(chatId)}/messages`, {
        method: 'POST',
        headers: _authH(),
        body: JSON.stringify({
            id: msg.id,
            role: msg.role,
            text: msg.text,
            status: msg.status || null,
            createdAt: new Date().toISOString(),
        }),
    });
}

async function patchChatTitle(chatId, title) {
    await fetch(`${_awpBase}/api/chats/${encodeURIComponent(chatId)}`, {
        method: 'PATCH',
        headers: _authH(),
        body: JSON.stringify({title}),
    });
}

const ChatBody = () => {
    const {chatId: routeChatId} = useParams();
    const navigate = useNavigate();
    const {setChatId} = usePolicyPageStore();
    const {user} = useSession() || {};

    const addMessage = useChatMessagesStore((s) => s.addMessage);
    const buildMessage = useChatMessagesStore((s) => s.buildMessage);
    const messagesForChat = useChatMessagesStore((s) => s.messagesByChat[routeChatId]);

    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);

    const lastMessage = messagesForChat?.length > 0 ? messagesForChat[messagesForChat.length - 1] : null;
    const isAssistantPending = Boolean(
        lastMessage
        && lastMessage.role === 'system'
        && (lastMessage.status === 'pending' || lastMessage.pending === true)
        && lastMessage.status !== 'error'
    );

    const {loading, error: fetchError} = useFetchMessages(routeChatId);

    const handleSend = useCallback(async (text) => {
        if (!text || isAssistantPending) return;
        setSending(true);
        setError(null);

        let chatId = routeChatId;
        let isNewChat = false;
        if (!chatId) {
            chatId = nanoid();
            isNewChat = true;
        }

        const isFirstMessage = !messagesForChat || messagesForChat.length === 0;

        const userMsg = buildMessage({chatId, text});
        userMsg.role = 'user';
        addMessage(chatId, userMsg);

        const systemMsg = buildMessage({chatId, text: ''});
        systemMsg.role = 'system';
        systemMsg.status = 'pending';
        addMessage(chatId, systemMsg);

        if (isNewChat) {
            setChatId(chatId);
            navigate(chatId, {replace: false});
        }

        try {
            const title = text.length > 60 ? text.slice(0, 60) + '…' : text;
            if (isNewChat) {
                await createFintelChat(chatId, title, user?.uid);
            } else if (isFirstMessage) {
                patchChatTitle(chatId, title).catch((err) => console.error('Failed to update chat title', err));
            }
            await persistMessage(chatId, userMsg);
            await persistMessage(chatId, systemMsg);
        } catch (err) {
            console.error('Failed to send message', err);
            setError(err.message || 'Failed to send');
        } finally {
            setSending(false);
        }
    }, [routeChatId, isAssistantPending, messagesForChat, buildMessage, addMessage, setChatId, navigate, user]);

    if (routeChatId && loading) {
        return (
            <Stack flexGrow={1} sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <CircularProgress/>
            </Stack>
        );
    }

    if (routeChatId && fetchError) {
        return (
            <Stack flexGrow={1} sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <Typography variant="body2" color="error">
                    Error loading messages: {fetchError.message}
                </Typography>
            </Stack>
        );
    }

    if (routeChatId && !loading) {
        return (
            <Stack overflow={'hidden'} flexGrow={1}>
                <Messages/>
                <Divider/>
                <MessageComposer
                    onSubmit={handleSend}
                    placeholder="Ask a question"
                    disabled={sending || isAssistantPending}
                    submitDisabled={sending || isAssistantPending}
                />
                {error && (
                    <Typography variant="caption" color="error" px={2} pb={1}>
                        {error}
                    </Typography>
                )}
            </Stack>
        );
    }
};

export default ChatBody;
